// ============================================================
// CppQuiz.js
//
// Gamified C++ Mastery Quiz — ProDev Platform
//
// Adapted from JavaQuiz.js, PythonQuiz.js, JavaScriptQuiz.js
// Same engine — brand new C++ question bank covering:
//   Syntax & Basics, Variables & Data Types, Control Flow,
//   Functions, Arrays & Pointers, OOP & Classes,
//   Inheritance & Polymorphism, Templates, STL,
//   Memory Management, Exception Handling,
//   File Handling, Multithreading
//
// Features (identical engine across all language quizzes):
//   - Personalised welcome screen (greets user by name)
//   - Rules & conduct screen with time limits explained
//   - Web Speech API — audible countdown before quiz starts
//   - Dynamic difficulty: re-arranges easy → hard on retry
//     based on initial performance per category
//   - Star reward system:
//       ★★★ = 100%  → Golden Buzzer + Certification Exam unlocked
//       ★★  = 90%+  → Certification Exam unlocked
//       ★   = 80%+  → Certification Exam unlocked
//       ✗   = <80%  → must retake (dynamically reordered)
//   - Anti-cheat engine:
//       • Tab/window visibility detection
//       • Browser focus loss detection
//       • Right-click disabled during quiz
//       • Copy/paste keyboard shortcuts blocked
//       • DevTools size-change detection
//       • Violations logged and shown on results screen
//   - Sound synthesis (Web Audio API — no external files):
//       • Correct answer chime
//       • Wrong answer buzz
//       • Timer warning beep (last 60 seconds)
//       • Quiz complete fanfare
//       • Golden buzzer sound (100% score)
//   - Category-level performance breakdown
//   - Leaderboard (localStorage — separate from other quizzes)
//   - Brand colours: #100720 dark, #E5C95F gold
//
// Junior dev notes throughout — explaining every concept,
// hook, and pattern used in this file.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ── MUI Icons ─────────────────────────────────────────────────
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SchoolIcon             from '@mui/icons-material/School';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

// ============================================================
//  BRAND CONSTANTS
//  Same palette as Header, Vision, Mission and all other quizzes.
//  Change once here — updates the entire platform.
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
    warning:    '#FF9F43',
};

// ============================================================
//  QUIZ CONFIGURATION
//  All behaviour tuning in one place.
//  leaderboardKey is unique — C++ scores stay separate.
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 1200,   // 20 minutes
    warningThreshold: 60,     // Seconds left when timer turns red
    passMark:         0.80,   // 80% = 1 star minimum
    twoStarMark:      0.90,   // 90% = 2 stars
    threeStarMark:    1.00,   // 100% = 3 stars + Golden Buzzer
    maxViolations:    3,      // Auto-terminate after 3 violations
    leaderboardKey:   'cpp_quiz_leaderboard',
};

// ============================================================
//  C++ QUESTION BANK
//
//  13 categories × 3 questions = 39 questions total.
//  difficulty: 'easy' | 'medium' | 'hard'
//  Used by reorderByDifficulty() on retry to adapt question order.
//
//  Junior dev note: Data lives here, UI lives below.
//  Keeping them separate makes it easy to later move questions
//  to a database or JSON file — just replace this array.
// ============================================================
const cppQuestionBank = [

    // ── Syntax & Basics ───────────────────────────────────────
    {
        question: 'Which header file is required to use cout in C++?',
        options: ['<stdio.h>', '<iostream>', '<conio.h>', '<stdlib.h>'],
        answer: '<iostream>',
        explanation: '`#include <iostream>` provides `std::cout`, `std::cin`, and `std::endl`.',
        link: '/cpp-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'Which statement correctly outputs "Hello" to the console in C++?',
        options: [
            'print("Hello");',
            'Console.WriteLine("Hello");',
            'cout << "Hello";',
            'printf << "Hello";',
        ],
        answer: 'cout << "Hello";',
        explanation: '`cout` with the insertion operator `<<` sends output to the standard output stream.',
        link: '/cpp-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'What does the `::` operator signify in C++?',
        options: [
            'Pointer dereference',
            'Scope resolution — accessing a member of a namespace or class',
            'Bitwise OR',
            'Member access on a pointer',
        ],
        answer: 'Scope resolution — accessing a member of a namespace or class',
        explanation: '`std::cout` uses `::` to access `cout` from the `std` namespace.',
        link: '/cpp-syntax',
        category: 'Syntax & Basics',
        difficulty: 'medium',
    },

    // ── Variables & Data Types ────────────────────────────────
    {
        question: 'Which data type stores a single character in C++?',
        options: ['string', 'char', 'byte', 'letter'],
        answer: 'char',
        explanation: '`char` stores a single character using 1 byte of memory.',
        link: '/cpp-datatypes',
        category: 'Variables & Data Types',
        difficulty: 'easy',
    },
    {
        question: 'What is the size of an `int` on most 64-bit systems?',
        options: ['2 bytes', '4 bytes', '8 bytes', '16 bytes'],
        answer: '4 bytes',
        explanation: 'On most modern 64-bit systems, `int` is 4 bytes (32 bits).',
        link: '/cpp-datatypes',
        category: 'Variables & Data Types',
        difficulty: 'medium',
    },
    {
        question: 'What does the `const` keyword do when applied to a variable?',
        options: [
            'Makes it a global variable',
            'Allocates it on the heap',
            'Prevents the variable from being modified after initialisation',
            'Makes the variable static',
        ],
        answer: 'Prevents the variable from being modified after initialisation',
        explanation: '`const int x = 5;` — any attempt to reassign `x` results in a compile-time error.',
        link: '/cpp-datatypes',
        category: 'Variables & Data Types',
        difficulty: 'easy',
    },

    // ── Control Flow ──────────────────────────────────────────
    {
        question: 'Which loop in C++ is guaranteed to execute its body at least once?',
        options: ['for', 'while', 'do-while', 'foreach'],
        answer: 'do-while',
        explanation: '`do { ... } while (condition);` executes the body before checking the condition.',
        link: '/cpp-control-flow',
        category: 'Control Flow',
        difficulty: 'easy',
    },
    {
        question: 'What is the purpose of the `break` statement in a switch block?',
        options: [
            'Exits the program',
            'Skips to the next case',
            'Exits the switch block and prevents fall-through',
            'Restarts the switch',
        ],
        answer: 'Exits the switch block and prevents fall-through',
        explanation: 'Without `break`, execution falls through to the next case automatically.',
        link: '/cpp-control-flow',
        category: 'Control Flow',
        difficulty: 'easy',
    },
    {
        question: 'What does the range-based for loop `for (int x : arr)` do?',
        options: [
            'Iterates using an index variable',
            'Iterates over each element of the array directly',
            'Creates a copy of the array',
            'Sorts the array',
        ],
        answer: 'Iterates over each element of the array directly',
        explanation: 'The range-based for loop (C++11) iterates over each element — cleaner than index-based loops.',
        link: '/cpp-control-flow',
        category: 'Control Flow',
        difficulty: 'medium',
    },

    // ── Functions ─────────────────────────────────────────────
    {
        question: 'What is function overloading in C++?',
        options: [
            'Calling a function more than once',
            'Defining multiple functions with the same name but different parameter lists',
            'Overriding a base class function',
            'Using default parameter values',
        ],
        answer: 'Defining multiple functions with the same name but different parameter lists',
        explanation: 'C++ allows multiple functions with the same name as long as their signatures (parameter types/count) differ.',
        link: '/cpp-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What is a default argument in C++?',
        options: [
            'A parameter that is always zero',
            'A value automatically used when a caller does not provide that argument',
            'The first parameter of any function',
            'A global variable used inside a function',
        ],
        answer: 'A value automatically used when a caller does not provide that argument',
        explanation: '`void greet(string name = "World")` — calling `greet()` uses "World" as the default.',
        link: '/cpp-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What does passing by reference (`&`) allow in C++?',
        options: [
            'Passing a copy of the variable',
            'The function to modify the original variable',
            'Allocating the variable on the heap',
            'Making the parameter constant',
        ],
        answer: 'The function to modify the original variable',
        explanation: '`void increment(int &x)` — changes to `x` inside the function affect the original variable passed in.',
        link: '/cpp-functions',
        category: 'Functions',
        difficulty: 'medium',
    },

    // ── Arrays & Pointers ─────────────────────────────────────
    {
        question: 'What does the dereference operator `*` do with a pointer?',
        options: [
            'Returns the address of the pointer',
            'Creates a new pointer',
            'Accesses the value stored at the pointer\'s address',
            'Deletes the pointer',
        ],
        answer: 'Accesses the value stored at the pointer\'s address',
        explanation: 'If `int *p = &x;`, then `*p` reads or writes the value of `x` through the pointer.',
        link: '/cpp-pointers',
        category: 'Arrays & Pointers',
        difficulty: 'medium',
    },
    {
        question: 'What does the address-of operator `&` return?',
        options: [
            'The value of the variable',
            'The memory address of the variable',
            'A reference to the variable',
            'The size of the variable',
        ],
        answer: 'The memory address of the variable',
        explanation: '`int *p = &x;` — `&x` returns the memory address where `x` is stored.',
        link: '/cpp-pointers',
        category: 'Arrays & Pointers',
        difficulty: 'easy',
    },
    {
        question: 'What is a null pointer in C++?',
        options: [
            'A pointer pointing to zero-value data',
            'A pointer that has not been assigned an address (points to nothing)',
            'A pointer to a deleted object',
            'A pointer with negative address',
        ],
        answer: 'A pointer that has not been assigned an address (points to nothing)',
        explanation: '`int *p = nullptr;` — in C++11 and later, `nullptr` is the preferred null pointer constant.',
        link: '/cpp-pointers',
        category: 'Arrays & Pointers',
        difficulty: 'medium',
    },

    // ── OOP & Classes ─────────────────────────────────────────
    {
        question: 'What is a constructor in C++?',
        options: [
            'A function that destroys an object',
            'A special member function called automatically when an object is created',
            'A function that copies an object',
            'A static method of a class',
        ],
        answer: 'A special member function called automatically when an object is created',
        explanation: 'A constructor has the same name as the class and no return type. It initialises the object.',
        link: '/cpp-classes',
        category: 'OOP & Classes',
        difficulty: 'easy',
    },
    {
        question: 'What is the purpose of a destructor in C++?',
        options: [
            'To create a copy of an object',
            'To override a parent method',
            'To release resources when an object goes out of scope or is deleted',
            'To initialise static members',
        ],
        answer: 'To release resources when an object goes out of scope or is deleted',
        explanation: 'The destructor `~ClassName()` is called automatically — used to free memory, close files, etc.',
        link: '/cpp-classes',
        category: 'OOP & Classes',
        difficulty: 'medium',
    },
    {
        question: 'What is the default access specifier for members of a `class` in C++?',
        options: ['public', 'protected', 'private', 'internal'],
        answer: 'private',
        explanation: 'In a `class`, members are `private` by default. In a `struct`, they are `public` by default.',
        link: '/cpp-classes',
        category: 'OOP & Classes',
        difficulty: 'medium',
    },

    // ── Inheritance & Polymorphism ────────────────────────────
    {
        question: 'Which keyword is used to inherit from a base class in C++?',
        options: ['extends', 'implements', ':', 'inherits'],
        answer: ':',
        explanation: '`class Dog : public Animal` — the colon followed by the access specifier inherits from Animal.',
        link: '/cpp-inheritance',
        category: 'Inheritance & Polymorphism',
        difficulty: 'easy',
    },
    {
        question: 'What is a virtual function in C++?',
        options: [
            'A function that cannot be called',
            'A function declared in a base class that can be overridden in derived classes',
            'A function with no body',
            'A static member function',
        ],
        answer: 'A function declared in a base class that can be overridden in derived classes',
        explanation: '`virtual void speak()` enables polymorphism — the correct derived class version is called at runtime.',
        link: '/cpp-polymorphism',
        category: 'Inheritance & Polymorphism',
        difficulty: 'medium',
    },
    {
        question: 'What is a pure virtual function in C++?',
        options: [
            'A virtual function with a default implementation',
            'A function that is declared but not defined — making the class abstract',
            'A function that cannot be inherited',
            'A global virtual function',
        ],
        answer: 'A function that is declared but not defined — making the class abstract',
        explanation: '`virtual void speak() = 0;` — a pure virtual function forces derived classes to provide an implementation.',
        link: '/cpp-polymorphism',
        category: 'Inheritance & Polymorphism',
        difficulty: 'hard',
    },

    // ── Templates ─────────────────────────────────────────────
    {
        question: 'What is the purpose of templates in C++?',
        options: [
            'To write HTML inside C++',
            'To create generic functions or classes that work with any data type',
            'To define macros',
            'To import external libraries',
        ],
        answer: 'To create generic functions or classes that work with any data type',
        explanation: '`template<typename T> T add(T a, T b)` works for int, float, double — any type.',
        link: '/cpp-templates',
        category: 'Templates',
        difficulty: 'medium',
    },
    {
        question: 'What is the syntax to declare a function template in C++?',
        options: [
            'generic<T> T func(T a)',
            'template<typename T> T func(T a)',
            'T template func(T a)',
            '<T> func(T a)',
        ],
        answer: 'template<typename T> T func(T a)',
        explanation: '`template<typename T>` declares T as a placeholder type — replaced by the actual type when the function is called.',
        link: '/cpp-templates',
        category: 'Templates',
        difficulty: 'medium',
    },
    {
        question: 'What is template specialisation in C++?',
        options: [
            'Using a template inside a class',
            'Providing a custom implementation of a template for a specific type',
            'Deleting a template',
            'Inheriting from a template class',
        ],
        answer: 'Providing a custom implementation of a template for a specific type',
        explanation: 'Template specialisation lets you override the generic behaviour for a specific type like `char*` or `bool`.',
        link: '/cpp-templates',
        category: 'Templates',
        difficulty: 'hard',
    },

    // ── STL (Standard Template Library) ──────────────────────
    {
        question: 'Which STL container stores elements in a key-value pair?',
        options: ['vector', 'list', 'map', 'set'],
        answer: 'map',
        explanation: '`std::map<Key, Value>` stores sorted key-value pairs. `std::unordered_map` stores them unsorted.',
        link: '/cpp-stl',
        category: 'STL',
        difficulty: 'easy',
    },
    {
        question: 'What does `std::vector::push_back()` do?',
        options: [
            'Removes the last element',
            'Inserts an element at the front',
            'Appends an element to the end of the vector',
            'Sorts the vector',
        ],
        answer: 'Appends an element to the end of the vector',
        explanation: '`v.push_back(10)` adds 10 to the end of vector `v`, growing it if needed.',
        link: '/cpp-stl',
        category: 'STL',
        difficulty: 'easy',
    },
    {
        question: 'Which STL algorithm sorts a container in ascending order?',
        options: ['std::order()', 'std::sort()', 'std::arrange()', 'std::rank()'],
        answer: 'std::sort()',
        explanation: '`std::sort(v.begin(), v.end())` sorts the vector in-place using an introsort algorithm.',
        link: '/cpp-stl',
        category: 'STL',
        difficulty: 'medium',
    },

    // ── Memory Management ─────────────────────────────────────
    {
        question: 'Which operator allocates memory on the heap in C++?',
        options: ['malloc()', 'alloc()', 'new', 'create'],
        answer: 'new',
        explanation: '`int *p = new int(5);` allocates memory on the heap and returns a pointer to it.',
        link: '/cpp-memory',
        category: 'Memory Management',
        difficulty: 'easy',
    },
    {
        question: 'What happens if you forget to call `delete` after `new`?',
        options: [
            'The program crashes immediately',
            'A memory leak occurs — memory is never returned to the OS',
            'The destructor is still called',
            'The pointer becomes null',
        ],
        answer: 'A memory leak occurs — memory is never returned to the OS',
        explanation: 'Every `new` must be paired with `delete`. Forgetting causes memory leaks — the heap grows without bound.',
        link: '/cpp-memory',
        category: 'Memory Management',
        difficulty: 'medium',
    },
    {
        question: 'What is a smart pointer in C++?',
        options: [
            'A faster version of a regular pointer',
            'A pointer that automatically manages memory and deletes when no longer needed',
            'A pointer to a virtual function',
            'A pointer stored in the STL',
        ],
        answer: 'A pointer that automatically manages memory and deletes when no longer needed',
        explanation: '`std::unique_ptr` and `std::shared_ptr` (C++11) automatically free heap memory — preventing leaks.',
        link: '/cpp-memory',
        category: 'Memory Management',
        difficulty: 'hard',
    },

    // ── Exception Handling ────────────────────────────────────
    {
        question: 'Which keywords are used for exception handling in C++?',
        options: ['try / catch / throw', 'try / except / raise', 'error / handle / throw', 'try / handle / throw'],
        answer: 'try / catch / throw',
        explanation: 'C++ uses `try { }` to wrap risky code, `throw` to raise exceptions, and `catch` to handle them.',
        link: '/cpp-exceptions',
        category: 'Exception Handling',
        difficulty: 'easy',
    },
    {
        question: 'What does `catch(...)` mean in C++?',
        options: [
            'Catches only integer exceptions',
            'Catches all types of exceptions',
            'Catches only std::exception',
            'A syntax error',
        ],
        answer: 'Catches all types of exceptions',
        explanation: '`catch(...)` is a catch-all handler — it catches any exception type, including non-standard ones.',
        link: '/cpp-exceptions',
        category: 'Exception Handling',
        difficulty: 'medium',
    },
    {
        question: 'Which base class should custom C++ exceptions inherit from?',
        options: ['std::error', 'std::exception', 'std::runtime', 'std::throwable'],
        answer: 'std::exception',
        explanation: 'Inheriting from `std::exception` allows overriding `what()` and integrating with standard catch blocks.',
        link: '/cpp-exceptions',
        category: 'Exception Handling',
        difficulty: 'hard',
    },

    // ── File Handling ─────────────────────────────────────────
    {
        question: 'Which header is needed for file handling in C++?',
        options: ['<fileio>', '<stdio.h>', '<fstream>', '<cfile>'],
        answer: '<fstream>',
        explanation: '`#include <fstream>` provides `ifstream` (read), `ofstream` (write), and `fstream` (both).',
        link: '/cpp-files',
        category: 'File Handling',
        difficulty: 'easy',
    },
    {
        question: 'Which class is used to write to a file in C++?',
        options: ['ifstream', 'ofstream', 'fstream', 'filestream'],
        answer: 'ofstream',
        explanation: '`std::ofstream outFile("data.txt");` opens a file for writing.',
        link: '/cpp-files',
        category: 'File Handling',
        difficulty: 'easy',
    },
    {
        question: 'How do you check if a file was opened successfully in C++?',
        options: [
            'file.exists()',
            'file.is_open()',
            'file.opened()',
            'file.status() == OK',
        ],
        answer: 'file.is_open()',
        explanation: '`if (file.is_open()) { ... }` checks whether the file stream successfully opened the file.',
        link: '/cpp-files',
        category: 'File Handling',
        difficulty: 'medium',
    },

    // ── Multithreading ────────────────────────────────────────
    {
        question: 'Which header is required for multithreading in C++11?',
        options: ['<process>', '<concurrent>', '<thread>', '<parallel>'],
        answer: '<thread>',
        explanation: '`#include <thread>` provides `std::thread` for creating and managing threads in C++11 and later.',
        link: '/cpp-threads',
        category: 'Multithreading',
        difficulty: 'medium',
    },
    {
        question: 'What does `thread.join()` do in C++?',
        options: [
            'Creates a new thread',
            'Detaches the thread to run independently',
            'Waits for the thread to finish before continuing',
            'Cancels the thread',
        ],
        answer: 'Waits for the thread to finish before continuing',
        explanation: '`t.join()` blocks the calling thread until thread `t` completes its execution.',
        link: '/cpp-threads',
        category: 'Multithreading',
        difficulty: 'medium',
    },
    {
        question: 'What is a mutex used for in C++ multithreading?',
        options: [
            'Creating new threads',
            'Sharing data between processes',
            'Preventing multiple threads from accessing shared data simultaneously',
            'Scheduling thread priority',
        ],
        answer: 'Preventing multiple threads from accessing shared data simultaneously',
        explanation: '`std::mutex` provides mutual exclusion — `lock()` and `unlock()` protect critical sections from race conditions.',
        link: '/cpp-threads',
        category: 'Multithreading',
        difficulty: 'hard',
    },
];

// ============================================================
//  UTILITY FUNCTIONS
//  Pure functions — no side effects, identical logic across quizzes.
// ============================================================

/**
 * shuffle
 * Returns a new randomly ordered copy — does NOT mutate the original.
 */
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * getStars
 * Maps score/total to 0–3 stars using QUIZ_CONFIG thresholds.
 */
const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= QUIZ_CONFIG.threeStarMark) return 3;
    if (ratio >= QUIZ_CONFIG.twoStarMark)   return 2;
    if (ratio >= QUIZ_CONFIG.passMark)       return 1;
    return 0;
};

/**
 * getBadge
 * Returns a C++-themed badge label for results and leaderboard.
 */
const getBadge = (stars) => {
    if (stars === 3) return '⚙️ C++ Wizard';
    if (stars === 2) return '🔧 C++ Pro';
    if (stars === 1) return '📘 C++ Apprentice';
    return '🌱 C++ Novice';
};

/**
 * formatTime
 * Converts raw seconds to MM:SS display string.
 * e.g. 125 → "02:05"
 */
const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

/**
 * saveScore / loadScores
 * Persist top 5 scores to localStorage under the C++ key.
 * Separate from Java, Python, and JavaScript leaderboards.
 */
const saveScore = (entry) => {
    const prev    = JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem(QUIZ_CONFIG.leaderboardKey, JSON.stringify(updated));
};

const loadScores = () =>
    JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');

/**
 * reorderByDifficulty
 * Adaptive retry — weak categories (low accuracy) appear last.
 * Strong categories appear first as an easy warmup.
 */
const reorderByDifficulty = (questions, categoryScores) => {
    return [...questions].sort((a, b) => {
        const aRatio = categoryScores[a.category]
            ? categoryScores[a.category].correct / categoryScores[a.category].total
            : 0.5;
        const bRatio = categoryScores[b.category]
            ? categoryScores[b.category].correct / categoryScores[b.category].total
            : 0.5;
        return bRatio - aRatio;
    });
};

// ============================================================
//  SOUND ENGINE — Web Audio API
//
//  Synthesised entirely in the browser — zero audio files needed.
//  audioCtx is lazily created on the first user interaction to
//  comply with browser autoplay policies.
// ============================================================

let audioCtx = null;

/**
 * getAudioContext
 * Lazy init — creates AudioContext on first call.
 * Bracket notation avoids TypeScript `as any` syntax.
 */
const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
        audioCtx = new AudioContextClass();
    }
    return audioCtx;
};

/**
 * playTone — core audio primitive.
 * Creates oscillator → gain node → output.
 * Smooth fade in/out prevents audio clicks at note boundaries.
 */
const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    try {
        const ctx  = getAudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + duration);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + duration + 0.05);
    } catch (e) {
        console.warn('Sound playback failed:', e);
    }
};

/** Correct — ascending two-note chime */
const playCorrect = () => {
    playTone(523, 0.15, 'sine', 0.3, 0);
    playTone(659, 0.2,  'sine', 0.3, 0.12);
};

/** Wrong — descending square buzz */
const playWrong = () => {
    playTone(300, 0.1,  'square', 0.2, 0);
    playTone(200, 0.15, 'square', 0.2, 0.12);
};

/** Warning beep — plays every 10s in last 60 seconds */
const playWarningBeep = () => playTone(880, 0.08, 'sine', 0.25, 0);

/** Complete fanfare — ascending 4-note arpeggio */
const playComplete = () =>
    [523, 659, 784, 1047].forEach((f, i) => playTone(f, 0.25, 'sine', 0.35, i * 0.12));

/** Golden Buzzer — triumphant chord + rising arpeggio (100% only) */
const playGoldenBuzzer = () => {
    [523, 659, 784].forEach((f) => playTone(f, 0.6, 'sine', 0.25, 0));
    [523, 659, 784, 1047, 1319].forEach((f, i) =>
        playTone(f, 0.3, 'triangle', 0.3, 0.5 + i * 0.1)
    );
};

/**
 * speakText
 * Web Speech API — browser built-in text-to-speech.
 * No API key or library required. Silent fallback if unsupported.
 */
const speakText = (text, rate = 0.9) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter  = new SpeechSynthesisUtterance(text);
    utter.rate   = rate;
    utter.pitch  = 1.1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
};

// ============================================================
//  STYLED COMPONENTS
//  Identical visual language to all other language quizzes.
// ============================================================

/** pulseGold — glowing pulse on Begin button and Golden Buzzer */
const pulseGold = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(229,201,95,0.4); }
    50%       { box-shadow: 0 0 0 20px rgba(229,201,95,0); }
`;

/** shakeAnim — horizontal shake for wrong answers and violations */
const shakeAnim = keyframes`
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-8px); }
    40%       { transform: translateX(8px); }
    60%       { transform: translateX(-6px); }
    80%       { transform: translateX(6px); }
`;

const PageWrapper = styled(Box)({
    minHeight:       '100vh',
    backgroundColor: BRAND.dark,
    fontFamily:      "'Poppins', sans-serif",
    padding:         '24px 16px 48px',
    overflowX:       'hidden',
});

const QuizCard = styled(Card)({
    backgroundColor: BRAND.darkCard,
    border:          `1px solid ${BRAND.darkBorder}`,
    borderRadius:    '20px',
    backdropFilter:  'blur(10px)',
    maxWidth:        '860px',
    margin:          '0 auto',
    overflow:        'visible',
});

const GoldButton = styled(Button)({
    background:    `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color:         BRAND.dark,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    800,
    fontSize:      '0.9rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding:       '12px 28px',
    borderRadius:  '10px',
    transition:    'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform:  'translateY(-2px)',
        boxShadow:  '0 8px 20px rgba(229,201,95,0.3)',
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.1)',
        color:      'rgba(255,255,255,0.3)',
    },
});

const OutlineButton = styled(Button)({
    borderColor:   BRAND.gold,
    color:         BRAND.gold,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    700,
    borderRadius:  '10px',
    padding:       '10px 24px',
    transition:    'background 0.2s',
    '&:hover': {
        backgroundColor: BRAND.goldFaint,
        borderColor:     BRAND.goldenrod,
    },
});

const ViolationBanner = styled(Box)({
    backgroundColor: 'rgba(255,107,107,0.15)',
    border:          '1px solid rgba(255,107,107,0.4)',
    borderRadius:    '10px',
    padding:         '10px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    marginBottom:    '16px',
    animation:       `${shakeAnim} 0.4s ease`,
});

// ============================================================
//  STAR DISPLAY COMPONENT
// ============================================================
const StarDisplay = ({ stars, size = 'large' }) => {
    const fontSize = size === 'large' ? '2.5rem' : '1.2rem';
    return (
        <Box display="flex" gap={0.5} justifyContent="center">
            {[1, 2, 3].map((i) =>
                i <= stars
                    ? <StarIcon key={i} sx={{ fontSize, color: BRAND.gold, filter: 'drop-shadow(0 0 4px rgba(229,201,95,0.6))' }} />
                    : <StarBorderIcon key={i} sx={{ fontSize, color: 'rgba(255,255,255,0.2)' }} />
            )}
        </Box>
    );
};

// ============================================================
//  MAIN COMPONENT — CppQuiz
//
//  Add to App.js:
//    import CppQuiz from "./pages/Languages/Cpp/CppQuiz";
//    <Route path="/cpp-quiz" element={<CppQuiz />} />
// ============================================================
function CppQuiz() {

    // ── Phase & Identity ──────────────────────────────────────
    // 4 phases: 'welcome' → 'rules' → 'quiz' → results (finished=true)
    const [phase,      setPhase]     = useState('welcome');
    const [userName,   setUserName]  = useState('');
    const [nameInput,  setNameInput] = useState('');

    // ── Quiz State ────────────────────────────────────────────
    const [questions,   setQuestions]   = useState([]);
    const [current,     setCurrent]     = useState(0);
    const [score,       setScore]       = useState(0);
    const [selected,    setSelected]    = useState(null);
    const [showAnswer,  setShowAnswer]  = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [isRetry,     setIsRetry]     = useState(false);

    // Stores per-category accuracy from last attempt for adaptive reorder
    const [prevCategoryScores, setPrevCategoryScores] = useState({});

    // ── Timer ─────────────────────────────────────────────────
    const [timer,    setTimer]    = useState(QUIZ_CONFIG.totalTimeSeconds);
    const [finished, setFinished] = useState(false);

    // ── Anti-Cheat ────────────────────────────────────────────
    const [violations,    setViolations]    = useState(0);
    const [violationLog,  setViolationLog]  = useState([]);
    const [showViolation, setShowViolation] = useState(false);
    const [lastViolation, setLastViolation] = useState('');

    // ── Refs ──────────────────────────────────────────────────
    // Junior dev note: violationCountRef mirrors violations state
    // so event handlers always read the current count.
    // React state updates are async — refs are synchronous.
    const violationCountRef = useRef(0);
    const quizActiveRef     = useRef(false);
    const warningPlayedRef  = useRef(false);

    // ============================================================
    //  QUESTION INITIALISATION
    // ============================================================
    const initQuestions = useCallback((retry, catScores) => {
        let bank = cppQuestionBank.map(q => ({ ...q, options: shuffle(q.options) }));
        if (retry && Object.keys(catScores).length > 0) {
            bank = reorderByDifficulty(bank, catScores);
        } else {
            bank = shuffle(bank);
        }
        setQuestions(bank);
    }, []);

    // ============================================================
    //  ANTI-CHEAT ENGINE
    //  Attaches event listeners when phase === 'quiz'.
    //  Returns cleanup function to remove them.
    // ============================================================
    const recordViolation = useCallback((message) => {
        if (!quizActiveRef.current) return;
        violationCountRef.current += 1;
        const count = violationCountRef.current;
        setViolations(count);
        setViolationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
        setLastViolation(message);
        setShowViolation(true);
        playTone(440, 0.3, 'square', 0.3, 0);
        speakText('Violation detected. ' + message);
        setTimeout(() => setShowViolation(false), 4000);
        if (count >= QUIZ_CONFIG.maxViolations) {
            speakText('Maximum violations reached. Quiz terminated.');
            quizActiveRef.current = false;
            setFinished(true);
        }
    }, []);

    useEffect(() => {
        if (phase !== 'quiz') return;

        const handleVisibility  = () => { if (document.hidden) recordViolation('Tab switch or window minimised detected.'); };
        const handleBlur        = ()  => recordViolation('Browser window lost focus.');
        const handleContextMenu = (e) => { e.preventDefault(); recordViolation('Right-click attempt blocked.'); };
        const handleKeyDown     = (e) => {
            const blocked =
                (e.ctrlKey && ['c', 'v', 'u', 's', 'a', 'p'].includes(e.key.toLowerCase())) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()));
            if (blocked) {
                e.preventDefault();
                recordViolation(`Blocked shortcut: ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`);
            }
        };
        const handleResize = () => {
            if (window.outerHeight - window.innerHeight > 160 || window.outerWidth - window.innerWidth > 160) {
                recordViolation('Possible DevTools opened — browser size change detected.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur',          handleBlur);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown',     handleKeyDown);
        window.addEventListener('resize',        handleResize);
        quizActiveRef.current = true;

        // Always clean up listeners when quiz ends or component unmounts
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur',          handleBlur);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown',     handleKeyDown);
            window.removeEventListener('resize',        handleResize);
            quizActiveRef.current = false;
        };
    }, [phase, recordViolation]);

    // ============================================================
    //  COUNTDOWN TIMER
    // ============================================================
    useEffect(() => {
        if (phase !== 'quiz' || finished) return;
        if (timer <= 0) { speakText('Time is up. Quiz finished.'); setFinished(true); return; }
        if (timer <= QUIZ_CONFIG.warningThreshold && timer % 10 === 0) {
            playWarningBeep();
            if (timer === QUIZ_CONFIG.warningThreshold) speakText(`${QUIZ_CONFIG.warningThreshold} seconds remaining.`);
        }
        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [timer, finished, phase]);

    // ============================================================
    //  RESULTS TRIGGER
    // ============================================================
    useEffect(() => {
        if (!finished || questions.length === 0) return;
        quizActiveRef.current = false;
        const stars = getStars(score, questions.length);

        if (stars === 3) {
            playGoldenBuzzer();
            speakText(`Congratulations ${userName}! Perfect score! Golden Buzzer and three stars!`);
        } else {
            playComplete();
            speakText(`Well done ${userName}. You scored ${score} out of ${questions.length}. ${stars} star${stars !== 1 ? 's' : ''}.`);
        }

        const catStats = {};
        questions.forEach((q, i) => {
            if (!catStats[q.category]) catStats[q.category] = { correct: 0, total: 0 };
            catStats[q.category].total++;
            if (userAnswers[i] === q.answer) catStats[q.category].correct++;
        });
        setPrevCategoryScores(catStats);
        saveScore({ date: new Date().toLocaleString(), score, total: questions.length, timeRemaining: timer, badge: getBadge(stars), stars, name: userName });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finished]);

    // ============================================================
    //  EVENT HANDLERS
    // ============================================================
    const handleNameSubmit = () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        setUserName(trimmed);
        speakText(`Welcome ${trimmed}. Please read the rules carefully before you begin.`);
        setPhase('rules');
    };

    const handleStartQuiz = () => {
        initQuestions(isRetry, prevCategoryScores);
        setCurrent(0); setScore(0); setSelected(null);
        setShowAnswer(false); setUserAnswers([]);
        setTimer(QUIZ_CONFIG.totalTimeSeconds); setFinished(false);
        setViolations(0); setViolationLog([]);
        violationCountRef.current = 0;
        warningPlayedRef.current  = false;
        setPhase('quiz');

        // Audible countdown — staggered via setTimeout
        speakText(`Are you ready, ${userName}?`, 0.85);
        setTimeout(() => speakText('3', 1.2), 1800);
        setTimeout(() => speakText('2', 1.2), 2800);
        setTimeout(() => speakText('1', 1.2), 3800);
        setTimeout(() => speakText('Begin!', 1.0), 4800);
    };

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers(prev => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) { setScore(s => s + 1); playCorrect(); }
        else { playWrong(); }
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1); setShowAnswer(false); setSelected(null);
        } else { setFinished(true); }
    };

    const handleRetry = () => {
        setIsRetry(true);
        setPhase('rules');
        speakText(`Let's try again, ${userName}. Questions are ordered by your weakest areas.`);
    };

    // ============================================================
    //  DERIVED VALUES
    // ============================================================
    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    const stars          = finished ? getStars(score, questions.length) : 0;
    const passed         = stars >= 1;
    const isGoldenBuzzer = stars === 3;
    const progressPct    = questions.length > 0 ? (current / questions.length) * 100 : 0;
    const timerWarning   = timer <= QUIZ_CONFIG.warningThreshold;

    // ============================================================
    //  RENDER — WELCOME SCREEN
    // ============================================================
    if (phase === 'welcome') {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={4}>
                            <Typography sx={{ fontSize: '3rem', lineHeight: 1 }}>⚙️</Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: BRAND.white, letterSpacing: '-0.02em', mt: 1 }}>
                                C++ Mastery Quiz
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                ProDev Adaptive Learning Platform
                            </Typography>
                            <Box mt={2}><StarDisplay stars={3} size="large" /></Box>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 4 }} />

                        <Box maxWidth={420} mx="auto">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '1rem', color: BRAND.white, mb: 0.5 }}>
                                Enter your full name to begin
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                Your name will appear on your certificate and leaderboard entry.
                            </Typography>

                            <Box
                                component="input"
                                placeholder="e.g. Kofi Boateng"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                                sx={{
                                    width: '100%', p: '14px 18px', borderRadius: '10px',
                                    border: `1px solid ${BRAND.darkBorder}`,
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: BRAND.white, fontFamily: "'Poppins', sans-serif",
                                    fontSize: '1rem', outline: 'none', mb: 3, boxSizing: 'border-box',
                                    '&:focus':        { borderColor: BRAND.gold },
                                    '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                }}
                            />

                            <GoldButton fullWidth onClick={handleNameSubmit} disabled={!nameInput.trim()} endIcon={<ArrowForwardIcon />}>
                                Continue to Rules
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RULES SCREEN
    // ============================================================
    if (phase === 'rules') {
        const rules = [
            { icon: <TimerIcon />,               title: 'Time Limit',         text: `You have ${QUIZ_CONFIG.totalTimeSeconds / 60} minutes to answer all ${cppQuestionBank.length} questions. Timer starts immediately when you click Begin.` },
            { icon: <StarIcon />,                 title: 'Star Rewards',       text: 'Score 80%+ for ★, 90%+ for ★★, 100% for ★★★ (Golden Buzzer!). Earn at least ★ to unlock the Final Certification Exam.' },
            { icon: <ReplayIcon />,               title: 'Failed Attempts',    text: 'Score below 80% and you must retake. Questions reorder based on your weakest categories — easiest first.' },
            { icon: <SecurityIcon />,             title: 'Anti-Cheat Rules',   text: `No tab switching, minimising, DevTools, right-clicking, or Ctrl+C/V. ${QUIZ_CONFIG.maxViolations} violations auto-terminate your quiz.` },
            { icon: <NotificationsActiveIcon />,  title: 'Audio Enabled',      text: 'Audio feedback and speech synthesis are active. Keep your volume on. A countdown will play before the first question.' },
            { icon: <SchoolIcon />,               title: 'Academic Integrity', text: 'Do not search for answers online. This quiz tests YOUR C++ knowledge. Your certificate only means something if you earn it honestly.' },
        ];

        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.gold }}>
                                Welcome, {userName}! 👋
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem' }}>
                                {isRetry ? 'Retaking with adaptive difficulty — weak categories come last.' : 'Read the rules carefully before you begin.'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 3 }} />

                        {rules.map((rule, i) => (
                            <Box key={i} display="flex" gap={2} alignItems="flex-start" mb={2}
                                 sx={{ p: 2, backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '12px' }}
                            >
                                <Box sx={{ color: BRAND.gold, mt: 0.2, flexShrink: 0 }}>{rule.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: BRAND.white, mb: 0.3 }}>
                                        {rule.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                        {rule.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: BRAND.darkBorder, my: 3 }} />

                        <Box textAlign="center">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: BRAND.white, mb: 3 }}>
                                Are you ready, {userName}? A countdown will play before the first question.
                            </Typography>
                            <GoldButton onClick={handleStartQuiz} startIcon={<PlayArrowIcon />}
                                        sx={{ fontSize: '1rem', px: 5, py: 1.5, animation: `${pulseGold} 2s ease-in-out infinite` }}
                            >
                                I'm Ready — Begin!
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RESULTS SCREEN
    // ============================================================
    if (finished) {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                        {isGoldenBuzzer && (
                            <Box textAlign="center" sx={{
                                background: 'linear-gradient(135deg, rgba(229,201,95,0.2), rgba(218,165,32,0.1))',
                                border: `2px solid ${BRAND.gold}`, borderRadius: '16px',
                                p: 3, mb: 3, animation: `${pulseGold} 1.5s ease-in-out infinite`,
                            }}>
                                <Typography sx={{ fontSize: '2.5rem' }}>🔔</Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: BRAND.gold }}>
                                    GOLDEN BUZZER!
                                </Typography>
                                <Typography sx={{ color: BRAND.dimWhite, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                    Perfect score! You are a C++ master, {userName}!
                                </Typography>
                            </Box>
                        )}

                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: BRAND.white, mb: 1 }}>
                                {passed ? '🎉 Well Done,' : '😓 Keep Going,'} {userName}!
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: BRAND.dimWhite, mb: 2 }}>
                                Score: <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>{score}</Box> / {questions.length} ({Math.round((score / questions.length) * 100)}%)
                            </Typography>
                            <StarDisplay stars={stars} size="large" />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: BRAND.gold, mt: 1.5, fontWeight: 600 }}>
                                {getBadge(stars)}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mt: 0.5 }}>
                                ⏱ Time remaining: {formatTime(timer)}
                            </Typography>
                        </Box>

                        {passed ? (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(62,207,142,0.08)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <EmojiEventsIcon sx={{ color: BRAND.success, fontSize: 32, mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.success, fontSize: '0.95rem', mb: 2 }}>
                                    You are eligible for the C++ Certification Exam!
                                </Typography>
                                <GoldButton component={Link} to="/cpp-certification-exam" endIcon={<ArrowForwardIcon />}>
                                    Attempt Certification Exam
                                </GoldButton>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.95rem', mb: 0.5 }}>
                                    You need 80%+ to unlock the C++ Certification Exam.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                    Retake reorders questions — easier topics first based on your weak areas.
                                </Typography>
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake Quiz (Adaptive)
                                </OutlineButton>
                            </Box>
                        )}

                        {violations > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '12px' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.88rem', mb: 1 }}>
                                    ⚠ {violations} Violation{violations > 1 ? 's' : ''} Detected
                                </Typography>
                                {violationLog.map((v, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.7)', lineHeight: 1.7 }}>
                                        {v}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            Category Breakdown
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {Object.entries(categoryStats).map(([cat, data]) => {
                                const pct      = Math.round((data.correct / data.total) * 100);
                                const barColor = pct >= 80 ? BRAND.success : pct >= 50 ? BRAND.warning : BRAND.error;
                                return (
                                    <Box key={cat} sx={{ p: '10px 16px', backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '10px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: BRAND.white }}>{cat}</Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: barColor, fontWeight: 700 }}>
                                                {data.correct}/{data.total} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={pct} sx={{
                                            height: 4, borderRadius: 2,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                            '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 2 },
                                        }} />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            🏅 Leaderboard — Top 5
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {loadScores().map((entry, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 16px',
                                    backgroundColor: idx === 0 ? 'rgba(229,201,95,0.1)' : BRAND.goldFaint,
                                    border: `1px solid ${idx === 0 ? BRAND.gold : BRAND.darkBorder}`,
                                    borderRadius: '10px',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: idx === 0 ? BRAND.gold : BRAND.white }}>
                                            #{idx + 1} {entry.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.date} • ⏱ {formatTime(entry.timeRemaining)} left
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <StarDisplay stars={entry.stars} size="small" />
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.score}/{entry.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <OutlineButton component={Link} to="/home" variant="outlined">Back to Home</OutlineButton>
                            {passed && (
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake for Better Score
                                </OutlineButton>
                            )}
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — ACTIVE QUIZ SCREEN
    // ============================================================
    if (phase === 'quiz' && questions.length > 0) {
        const q = questions[current];

        return (
            <PageWrapper>
                {/* Sticky header */}
                <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: BRAND.dark, borderBottom: `1px solid ${BRAND.darkBorder}`, py: 1.5, px: 2, mb: 2 }}>
                    <Box maxWidth="860px" mx="auto" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: BRAND.dimWhite }}>
                            Question <Box component="span" sx={{ color: BRAND.gold }}>{current + 1}</Box> of {questions.length}
                        </Typography>
                        <Chip label={q.category} size="small" sx={{ backgroundColor: BRAND.goldFaint, color: BRAND.gold, border: `1px solid ${BRAND.darkBorder}`, fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.7rem' }} />
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TimerIcon sx={{ color: timerWarning ? BRAND.error : BRAND.gold, fontSize: '1.1rem' }} />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1rem', color: timerWarning ? BRAND.error : BRAND.gold, animation: timerWarning ? `${pulseGold} 1s ease-in-out infinite` : 'none' }}>
                                {formatTime(timer)}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.dimWhite }}>
                            Score: <Box component="span" sx={{ color: BRAND.gold }}>{score}</Box>
                        </Typography>
                    </Box>
                    <Box maxWidth="860px" mx="auto" mt={1}>
                        <LinearProgress variant="determinate" value={progressPct} sx={{
                            height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.06)',
                            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`, borderRadius: 2 },
                        }} />
                    </Box>
                </Box>

                {/* Violation banner */}
                {showViolation && (
                    <Box maxWidth="860px" mx="auto" px={2} mb={1}>
                        <ViolationBanner>
                            <WarningAmberIcon sx={{ color: BRAND.error, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.error }}>
                                    ⚠ Violation #{violations} Detected
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.8)' }}>
                                    {lastViolation} — {QUIZ_CONFIG.maxViolations - violations} more will terminate this quiz.
                                </Typography>
                            </Box>
                        </ViolationBanner>
                    </Box>
                )}

                {/* Question card */}
                <QuizCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <Typography sx={{
                            fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                            fontSize: { xs: '1rem', md: '1.15rem' }, color: BRAND.white,
                            mb: 3, lineHeight: 1.6, p: 2.5,
                            backgroundColor: BRAND.goldFaint,
                            borderLeft: `3px solid ${BRAND.gold}`,
                            borderRadius: '0 10px 10px 0',
                        }}>
                            {current + 1}. {q.question}
                        </Typography>

                        <Box>
                            {q.options.map((opt, i) => {
                                const isCorrect = showAnswer && opt === q.answer;
                                const isWrong   = showAnswer && opt === selected && opt !== q.answer;
                                return (
                                    <Button key={i} onClick={() => handleAnswer(opt)} disabled={showAnswer}
                                            startIcon={
                                                isCorrect ? <CheckCircleIcon sx={{ color: BRAND.success }} /> :
                                                    isWrong   ? <CancelIcon      sx={{ color: BRAND.error   }} /> : null
                                            }
                                            sx={{
                                                width: '100%', textAlign: 'left', justifyContent: 'flex-start',
                                                p: '14px 20px', borderRadius: '10px',
                                                fontFamily: "'Poppins', sans-serif", fontWeight: 500, fontSize: '0.9rem',
                                                textTransform: 'none', mb: 1,
                                                backgroundColor: isCorrect ? 'rgba(62,207,142,0.15)' : isWrong ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.error : 'rgba(255,255,255,0.12)'}`,
                                                color: isCorrect ? BRAND.success : isWrong ? BRAND.error : BRAND.white,
                                                transition: 'all 0.25s ease',
                                                animation: isWrong ? `${shakeAnim} 0.4s ease` : 'none',
                                                '&:hover': !showAnswer ? { backgroundColor: 'rgba(229,201,95,0.08)', borderColor: BRAND.gold } : {},
                                                '&.Mui-disabled': { opacity: 1 },
                                            }}
                                    >
                                        {opt}
                                    </Button>
                                );
                            })}
                        </Box>

                        {showAnswer && (
                            <Box sx={{
                                mt: 2, p: 2,
                                backgroundColor: selected === q.answer ? 'rgba(62,207,142,0.06)' : 'rgba(255,107,107,0.06)',
                                border: `1px solid ${selected === q.answer ? 'rgba(62,207,142,0.3)' : 'rgba(255,107,107,0.3)'}`,
                                borderRadius: '10px',
                            }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: selected === q.answer ? BRAND.success : BRAND.error, mb: 0.5 }}>
                                    {selected === q.answer ? '✅ Correct!' : `❌ Incorrect — Correct answer: ${q.answer}`}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {q.explanation}
                                </Typography>
                                <Typography component={Link} to={q.link} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: BRAND.gold, mt: 0.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    📖 Review this topic →
                                </Typography>
                            </Box>
                        )}

                        {showAnswer && (
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <GoldButton onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                                    {current + 1 < questions.length ? 'Next Question' : 'Finish Quiz'}
                                </GoldButton>
                            </Box>
                        )}
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // Fallback — should never appear in normal flow
    return (
        <PageWrapper>
            <Typography sx={{ color: BRAND.white, textAlign: 'center', pt: 10, fontFamily: "'Poppins', sans-serif" }}>
                Loading quiz...
            </Typography>
        </PageWrapper>
    );
}

export default CppQuiz;
